import { SxProps, Theme } from '@mui/material';
import { ErrorBoundary } from '@sentry/react';
import React, { useCallback, useContext, useState } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { AppConfigDetailDTO, CallOutcomeEnum } from '_lib/api/apiTypes.shim';
import { useCallLogNavigate } from '_lib/callPop/useCallLogNavigate';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Box } from '_lib/components/Box/Box';
import { ErrorBoundaryFallback } from '_lib/components/ErrorBoundaryFallback/ErrorBoundaryFallback';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { Stack } from '_lib/components/Stack/Stack';
import { TextField } from '_lib/components/TextField/TextField';

import { Typography } from '_lib/components/Typography/Typography';
import { useAuthRoles } from '_lib/hooks/useAuthRoles';
import { useDebouncedOnChange } from '_lib/hooks/useDebouncedOnChange';

import { useAppDispatch, useAppSelector } from '_lib/redux/hooks';
import { FlexCard } from '_lib/styles/PhoneCall.styled';
import { toSxArray } from '_lib/utils/helpers';

import symbolWhiteImage from '../../../../../../assets/img/symbol_white.png';

import {
  selectPhoneCallContext,
  selectPhoneCallFieldIssues,
  setPartialPhoneCallContext
} from '../../StPortalPhoneCall.slice';
import { handledIssues } from '../CallDetailsBox/CallDetailsBox';

interface PortalIFramePaneProps {
  iframeURL: string | undefined;
  iframePaneRef: React.RefObject<null>;
  companyId: string;
  companyName: string;
  sx?: SxProps<Theme>;
}

const TitleText = (props: { text: string }) => {
  return (
    <Box
      sx={[
        (theme) => ({
          padding: 2,
          bgcolor: theme.palette.primary.main,
          color: theme.palette.error.contrastText,
          borderRadius: 2
        })
      ]}>
      <Typography text={props.text} align='center' variant='h3' />
    </Box>
  );
};

const isCallPopShellCompany = (appConfig: NonNullable<AppConfigDetailDTO>, companyId: string) =>
  !!appConfig.companyConfigItems &&
  appConfig.companyConfigItems.some((item) => item.isCallPopShellCompany && item.companyId === companyId);

const ShellCompanyWarning = ({ companyName }: { companyName: string }) => {
  return (
    <Stack
      style={{ width: '100%', height: '100%' }}
      sx={[
        (theme) => ({ backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, padding: 2 })
      ]}>
      <TitleText text={`MAIN COMPANY: ${companyName}`} />
      <Typography
        text={[
          `You must enter a zip code on the right to find the correct location for ${companyName}.`,
          'Once you switch to the correct sister company for the zip code you will get access to Service Titan.'
        ].join(' ')}
      />
    </Stack>
  );
};

export const PortalIframePane = (props: PortalIFramePaneProps): React.ReactElement => {
  const { iframeURL, iframePaneRef, companyId, companyName, sx } = props;

  const [isLoadingIframe, setIsLoadingIframe] = useState(true);

  const issues = useAppSelector(selectPhoneCallFieldIssues);
  const phoneCallContext = useAppSelector(selectPhoneCallContext);

  const dispatch = useAppDispatch();

  const { navigateToCallLog } = useCallLogNavigate();

  const authRoles = useAuthRoles();

  const api = useContext(ApiContext);
  const { data: appConfigData } = api.appConfig.useCachedAppConfigQuery();

  const cachedInternalNotesUpdate = useCallback(
    (internalNotes: string) => {
      dispatch(setPartialPhoneCallContext({ updates: { internalNotes }, clearIssues: [handledIssues.internalNotes] }));
    },
    [dispatch]
  );
  const { text: internalNotesText, onChangeDebounced: handleSetInternalNotesDebounced } = useDebouncedOnChange(
    cachedInternalNotesUpdate,
    { initialValue: phoneCallContext.internalNotes }
  );

  const isShellCompany = appConfigData ? isCallPopShellCompany(appConfigData, companyId) : undefined;

  return (
    <FlexBox
      sx={[
        (theme) => ({
          bgcolor: theme.palette.callPane.main,
          color: theme.palette.callPane.contrastText,
          flexDirection: 'column',
          overflow: 'auto',
          height: '100%',
          width: '100%',
          position: 'absolute'
        }),
        ...toSxArray(sx)
      ]}>
      <ErrorBoundary
        beforeCapture={(scope) => {
          scope.setTag('pane', 'Portal Iframe Pane');
        }}
        fallback={({ resetError }) => (
          <ErrorBoundaryFallback
            title='Error Displaying Portal Iframe'
            onRefresh={() => {
              resetError();
            }}
          />
        )}>
        <FlexBox sx={{ flexGrow: 1, position: 'relative' }} ref={iframePaneRef}>
          {/* Don't load iframe till we have AppConfig data and an iframeURL */}
          {(!appConfigData || !iframeURL) && (
            <iframe key={companyId} style={{ width: '100%', height: '100%' }} onLoad={() => {}}></iframe>
          )}
          {/* If company isn't a shell company, load the iframe as per normal */}
          {appConfigData && iframeURL && !isShellCompany && (
            <iframe
              key={companyId}
              style={{ width: '100%', height: '100%' }}
              src={iframeURL}
              onLoad={() => setIsLoadingIframe(false)}></iframe>
          )}
          {/* If company isn't a shell company, load the iframe as per normal */}
          {appConfigData && isShellCompany && <ShellCompanyWarning companyName={companyName} />}
        </FlexBox>
        <BackdropSpinner open={!isShellCompany && isLoadingIframe} />
        {(isShellCompany || !isLoadingIframe) && (
          <Stack direction={'row'} spacing={2} sx={{ flexGrow: 'inherit', alignItems: 'center', padding: 1 }}>
            <IconButton
              disabled={authRoles.isCallCenterUser || authRoles.isAccountUser}
              onClick={() => navigateToCallLog()}>
              <img src={symbolWhiteImage} alt='Logo' style={{ width: '30px' }} />
            </IconButton>
            <FlexBox sx={{ flexGrow: 1 }}>
              <FlexCard variant='outlined'>
                <FlexBox sx={{ margin: 0.75, flexGrow: 1 }}>
                  <TextField
                    fullWidth
                    label='Call Center Internal Notes'
                    multiline
                    minRows={3}
                    maxRows={3}
                    required={
                      phoneCallContext.sendEscalationEmail ||
                      phoneCallContext.callOutcome === CallOutcomeEnum.Miscellaneous
                    }
                    value={internalNotesText}
                    error={{ issuePath: handledIssues.internalNotes, issues }}
                    onChange={(e) => handleSetInternalNotesDebounced(e.target.value)}
                  />
                </FlexBox>
              </FlexCard>
            </FlexBox>
          </Stack>
        )}
      </ErrorBoundary>
    </FlexBox>
  );
};
